import { IActivity } from '@eduwrite/shared';
import { useAuthStore } from '../store/useAuthStore';
import { fetchWithRetry } from '../utils/fetchWithRetry';

interface PendingTaskRow {
    assignmentId?: string;
    student_assignment_id?: string;
    id?: string;
    activity_id?: string;
    title: string;
    type: string;
    mechanic?: string;
    config: unknown;
    isDynamic?: boolean;
    is_dynamic?: boolean;
    accessibilitySettings?: { textToSpeechPolicy?: 'year_group_default' | 'on' | 'off' };
    accessibility_settings?: { textToSpeechPolicy?: 'year_group_default' | 'on' | 'off' };
    feedbackMode?: boolean;
    feedback_mode?: boolean;
}

/**
 * Service responsible for fetching a student's DB-persisted pending task queue
 * from the sync-server, and resolving student IDs from the class roster.
 */
export const TaskQueueService = {
    /**
     * Fetch all pending tasks for a student from the sync-server.
     * Maps raw DB rows to IActivity objects for the ActivityStore.
     */
    async fetchPendingTasks(studentId: string, syncServerUrl: string): Promise<IActivity[]> {
        try {
            const apiBase = String(syncServerUrl).replace(/\/+$/, '');
            const deviceToken = useAuthStore.getState().deviceToken;
            const res = await fetchWithRetry(`${apiBase}/api/students/${studentId}/tasks`, {
                headers: { Authorization: `Bearer ${deviceToken}` }
            });
            if (!res.ok) {
                console.warn(`[TaskQueueService] Failed to fetch tasks: ${res.status} ${res.statusText}`);
                throw new Error('Unable to load your activities. Please try again.');
            }
            const rows: PendingTaskRow[] = await res.json();
            if (rows.length > 0) {
                const fbMode = rows[0].feedbackMode ?? rows[0].feedback_mode;
                if (typeof fbMode === 'boolean') {
                    useAuthStore.getState().setClassFeedbackMode(fbMode);
                }
            }
            return rows.map((row) => ({
                id: row.id || row.activity_id || 'unknown',
                assignmentId: row.assignmentId || row.student_assignment_id || 'unknown',
                type: row.type as unknown,
                mechanic: row.mechanic as unknown,
                title: row.title,
                instructions: '',
                config: row.config ?? {},
                isDynamic: row.isDynamic ?? row.is_dynamic ?? false,
                classAccessibilitySettings: row.accessibilitySettings ?? row.accessibility_settings,
            } as IActivity));
        } catch (err: unknown) {
            console.error(`[TaskQueueService] Error fetching pending tasks for student ${studentId}:`, err);
            if (err instanceof Error && err.message === 'Unable to load your activities. Please try again.') throw err;
            throw new Error('Unable to load your activities. Please try again.');
        }
    },

    /**
     * Synchronise the current class's server-selected active lesson before loading a roster pupil's queue.
     */
    async syncActiveTasks(studentId: string, classId: string, syncServerUrl: string): Promise<void> {
        try {
            const apiBase = String(syncServerUrl).replace(/\/+$/, '');
            const deviceToken = useAuthStore.getState().deviceToken;
            const res = await fetchWithRetry(`${apiBase}/api/classes/${classId}/students/${studentId}/sync-active-tasks`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${deviceToken}` },
            });
            if (!res.ok) {
                console.warn(`[TaskQueueService] Failed to synchronise active tasks: ${res.status} ${res.statusText}`);
                throw new Error('Unable to load your activities. Please try again.');
            }
        } catch (error: unknown) {
            console.error(`[TaskQueueService] Error synchronising active tasks for student ${studentId}:`, error);
            if (error instanceof Error && error.message === 'Unable to load your activities. Please try again.') throw error;
            throw new Error('Unable to load your activities. Please try again.');
        }
    },

    /**
     * Given a student's display name and the class join code, resolves their
     * DB UUID by cross-referencing the enrolled roster. Returns null if not found.
     */
    async resolveStudentId(
        studentName: string,
        classId: string,
        syncServerUrl: string
    ): Promise<string | null> {
        try {
            const apiBase = String(syncServerUrl).replace(/\/+$/, '');
            const deviceToken = useAuthStore.getState().deviceToken;
            const res = await fetchWithRetry(`${apiBase}/api/classes/${classId}/students`, {
                headers: { Authorization: `Bearer ${deviceToken}` }
            });
            if (!res.ok) {
                console.warn(`[TaskQueueService] Failed to resolve student ID: ${res.status} ${res.statusText}`);
                return null;
            }
            const roster: Array<{ id: string; name: string }> = await res.json();
            const found = roster.find((s) => s.name === studentName);
            return found?.id ?? null;
        } catch (err: unknown) {
            console.error(`[TaskQueueService] Error resolving student ID for ${studentName} in class ${classId}:`, err);
            return null;
        }
    },

    /**
     * Resolve or create a student by name on the sync-server.
     * If the name matches an existing enrolled student exactly, returns 'found' with the existing UUID.
     * Otherwise creates and enrolls a new student immediately, returning 'created' with the new UUID.
     * This guarantees every manual name entry yields a real studentId, eliminating anonymous/deviceId gaps.
     */
    async resolveOrCreateStudent(
        studentName: string,
        classId: string,
        syncServerUrl: string
    ): Promise<{ action: 'found' | 'created'; studentId: string } | null> {
        try {
            const apiBase = String(syncServerUrl).replace(/\/+$/, '');
            const deviceToken = useAuthStore.getState().deviceToken;
            const res = await fetchWithRetry(`${apiBase}/api/classes/${classId}/students/resolve-or-create`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${deviceToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name: studentName }),
            });
            if (!res.ok) {
                console.warn(`[TaskQueueService] Failed to resolve-or-create student: ${res.status} ${res.statusText}`);
                return null;
            }
            const data = await res.json() as { action: 'found' | 'created'; studentId: string };
            return data;
        } catch (err: unknown) {
            console.error(`[TaskQueueService] Error resolve-or-create for ${studentName} in class ${classId}:`, err);
            return null;
        }
    },

    /**
     * Search for students with similar names in the same year group.
     * Returns 'found' (exact roster match), 'suggest' (list of candidates), or 'none'.
     */
    async resolveOrSuggestStudent(
        studentName: string,
        classId: string,
        syncServerUrl: string
    ): Promise<
        | { action: 'found'; studentId: string }
        | { action: 'suggest'; suggestions: Array<{ id: string; name: string; className: string }> }
        | { action: 'none' }
        | null
    > {
        try {
            const apiBase = String(syncServerUrl).replace(/\/+$/, '');
            const deviceToken = useAuthStore.getState().deviceToken;
            const res = await fetchWithRetry(`${apiBase}/api/classes/${classId}/students/resolve-or-suggest`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${deviceToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name: studentName }),
            });
            if (!res.ok) {
                console.warn(`[TaskQueueService] Failed to resolve-or-suggest student: ${res.status} ${res.statusText}`);
                return null;
            }
            return await res.json();
        } catch (err: unknown) {
            console.error(`[TaskQueueService] Error resolve-or-suggest for ${studentName} in class ${classId}:`, err);
            return null;
        }
    },

    /**
     * Enroll an existing student (found via year-group suggestion) into the current class.
     */
    async enrollExistingStudent(
        studentId: string,
        classId: string,
        syncServerUrl: string
    ): Promise<{ success: boolean; studentId: string } | null> {
        try {
            const apiBase = String(syncServerUrl).replace(/\/+$/, '');
            const deviceToken = useAuthStore.getState().deviceToken;
            const res = await fetchWithRetry(`${apiBase}/api/classes/${classId}/students/enroll-existing`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${deviceToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ studentId }),
            });
            if (!res.ok) {
                console.warn(`[TaskQueueService] Failed to enroll existing student: ${res.status} ${res.statusText}`);
                return null;
            }
            return await res.json() as { success: boolean; studentId: string };
        } catch (err: unknown) {
            console.error(`[TaskQueueService] Error enrolling existing student ${studentId}:`, err);
            return null;
        }
    },
};
